/**
 * Remembers the last Care Queue board a user viewed, so returning to the queue —
 * even after logging out and back in — restores their board instead of
 * defaulting to the first one. Client-only UI preference, keyed by user id so two
 * people sharing a browser never inherit each other's board. Survives logout: the
 * session logout only clears its own session keys, not this one.
 */
const keyFor = (userId: string): string => `behuman:careQueue:lastBoard:${userId}`

export function readLastBoard(userId: string): string | null {
  try {
    return globalThis.localStorage?.getItem(keyFor(userId)) ?? null
  } catch {
    // Private mode / sandboxed iframe — degrade to no memory.
    return null
  }
}

export function writeLastBoard(userId: string, boardId: string): void {
  try {
    globalThis.localStorage?.setItem(keyFor(userId), boardId)
  } catch {
    // Private mode / sandboxed iframe — silently degrade.
  }
}

/**
 * Same idea for the queue's row density: a coordinator who packs the rows in
 * wants them packed in tomorrow too, so the choice follows the user rather than
 * the session. Anything other than the stored 'compact' reads as the default.
 */
const densityKeyFor = (userId: string): string => `behuman:careQueue:density:${userId}`

export function readDensity(userId: string): 'roomy' | 'compact' {
  try {
    return globalThis.localStorage?.getItem(densityKeyFor(userId)) === 'compact'
      ? 'compact'
      : 'roomy'
  } catch {
    return 'roomy'
  }
}

export function writeDensity(userId: string, density: 'roomy' | 'compact'): void {
  try {
    globalThis.localStorage?.setItem(densityKeyFor(userId), density)
  } catch {
    // Private mode / sandboxed iframe — silently degrade.
  }
}

/**
 * Same idea for the Care Coordination tab itself: leaving the page and coming
 * back should land you where you were, rather than resetting to Care Queue.
 */
const tabKeyFor = (userId: string): string => `behuman:careCoordination:lastTab:${userId}`

export function readLastTab(userId: string): string | null {
  try {
    return globalThis.localStorage?.getItem(tabKeyFor(userId)) ?? null
  } catch {
    return null
  }
}

export function writeLastTab(userId: string, tab: string): void {
  try {
    globalThis.localStorage?.setItem(tabKeyFor(userId), tab)
  } catch {
    // Private mode / sandboxed iframe — silently degrade.
  }
}
